import { Box, Container, Tab, Tabs as Tabber, Typography } from "@mui/material";
import * as React from "react";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import config from "../../scripts/config";
import useBackend from "../../hooks/use_backend";
import {Outlet, useLocation, useNavigate, useNavigationType, useParams} from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Warning';
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {REACT_QUERY_KEYS} from "../../enums/react_query_key_enum";
import {NAVIGATION_PATH} from "../../enums/navigation_enum";
import { fetchSiteProperties } from "../../scripts/api_v2/properties_client";
import { fetchElionBoxStatus } from "../../scripts/api_v2/status_client";
import { useAmplitudeTrack, consumeSiteSelectionSource, siteSelectionEventName, getSitesCount, SITE_SELECTION_SOURCE } from "../../scripts/amplitude_utils";

export const Tabs = Object.freeze({
  ALERTS: NAVIGATION_PATH.ALERTS,
  COMMISSIONING: NAVIGATION_PATH.COMMISSIONING,
  GENERAL: NAVIGATION_PATH.GENERAL,
  DEVICES: NAVIGATION_PATH.DEVICES,
  INSTALLATION: NAVIGATION_PATH.INSTALLATION,
  SCHEMATIC: NAVIGATION_PATH.SCHEMATIC,
  FINANCIAL: NAVIGATION_PATH.FINANCIAL,
  METERING: NAVIGATION_PATH.METERING,
  LOG: NAVIGATION_PATH.LOG,
  STATISTICS: NAVIGATION_PATH.STATISTICS,
  DASHBOARD: NAVIGATION_PATH.DASHBOARD,
});

export default function SitePage() {
    const params = useParams();
    const location = useLocation()
    const navigationType = useNavigationType()
    const { trackEvent, trackSiteTabViewed, trackSiteTabLoaded } = useAmplitudeTrack();
    const currLoc = location.pathname.split('/').pop();
    const currTab = Object.values(Tabs).includes(currLoc) ? currLoc : Tabs.GENERAL
    const [tab, setTab] = React.useState(currTab)
    const backend = useBackend();
    const navigate = useNavigate()
    const queryClient = useQueryClient();
    const trackSiteTabLoadedRef = React.useRef(trackSiteTabLoaded);
    trackSiteTabLoadedRef.current = trackSiteTabLoaded;

    // One site_selected per opened site, whatever the way in (list, landing page cards, URL/history)
    const trackedSiteId = React.useRef(null);
    React.useEffect(() => {
      if (trackedSiteId.current === params.site_id) return;
      trackedSiteId.current = params.site_id;
      // key 'default' = first page of this app load (typed URL, bookmark, refresh, shared link)
      const source = consumeSiteSelectionSource(
        location.key === 'default' ? SITE_SELECTION_SOURCE.PAGE_LOAD
          : navigationType === 'POP' ? SITE_SELECTION_SOURCE.BROWSER_HISTORY
          : SITE_SELECTION_SOURCE.OTHER
      );
      trackEvent(siteSelectionEventName(source), {
        site_id: params.site_id,
        source,
        ...(getSitesCount() !== null ? { sites_count: getSitesCount() } : {}),
      });
    }, [params.site_id, trackEvent]);

    // Single source of truth for tab views: covers clicks, direct URLs and back/forward navigation
    React.useEffect(() => {
      trackSiteTabViewed(currTab, {
        site_id: params.site_id,
      });
    }, [currTab, params.site_id, trackSiteTabViewed]);

    const {data: elionBoxStatus} = useQuery({
        queryKey: [REACT_QUERY_KEYS.ELION_STATUS],
        queryFn: () => fetchElionBoxStatus(),
        enabled: true,
        refetchInterval: 300000
    });

    const {data: siteData, error: siteError, isLoading: siteLoading, refetch: siteRefetch} = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
        queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    });

    const {data: siteProps, error: sitePropsError, isLoading: sitePropsLoading, refetch: sitePropsRefetch} = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_PROPERTIES, backend.fetch, params.site_id],
        queryFn: () => fetchSiteProperties(backend.fetch, params.site_id),
    });

    React.useEffect(() => {
        siteRefetch();
        sitePropsRefetch();
    }, [params.site_id, siteRefetch, sitePropsRefetch]);

    // Tab load time: from opening the tab until every query that has no data yet (site data and the tab's own
    // data) is done. A tab that is fully cached is reported as (nearly) instant. Declared after the queries above
    // so their fetches have started when this runs.
    React.useEffect(() => {
        const start = performance.now();
        let done = false;
        const hasInitialLoadPending = () => queryClient.isFetching({
            predicate: (q) => q.state.data === undefined && q.queryKey[0] !== REACT_QUERY_KEYS.ELION_STATUS,
        }) > 0;
        // a failed load is not a load time: skip the event when an observed query ended without data
        const hasFailedLoad = () => queryClient.getQueryCache().findAll({
            predicate: (q) => q.state.data === undefined && q.state.status === 'error' && q.getObserversCount() > 0,
        }).length > 0;
        const finish = () => {
            if (done || hasInitialLoadPending()) return;
            done = true;
            unsubscribe();
            clearTimeout(timer);
            if (hasFailedLoad()) return;
            trackSiteTabLoadedRef.current(currTab, performance.now() - start, {
                site_id: params.site_id,
            });
        };
        const unsubscribe = queryClient.getQueryCache().subscribe(finish);
        // let the tab's own queries mount and start fetching before the first check
        const timer = setTimeout(finish, 0);
        return () => {
            done = true;
            unsubscribe();
            clearTimeout(timer);
        };
    }, [currTab, params.site_id, queryClient]);


    React.useEffect(() => {
        if (currLoc !== currTab) {
            navigate(currTab)
        }
        setTab(currTab)
    }, [currLoc])


    const openDashboard = () => {
        if (!siteData) return;
        trackEvent('dashboard_opened', {
          site_id: params.site_id,
        });
        const dashboardWindow = window.open(config.REACT_APP_DASH_URL + "/auto?token=" + siteData.site_id, "_blank");
        if (dashboardWindow) dashboardWindow.focus();
    };
    const formatDevices = (devices) => {
        return "Devices" + (devices ? ` (${Object.keys(devices).length})` : "");
    };

    const changeTab = (tabValue) => {
    if (tabValue === Tabs.DASHBOARD) {
      openDashboard();
    } else {
      setTab(tabValue);
      navigate(tabValue, { state: location.state });
    }
    };

  const connectionStatusText = () => {
    if (!siteData) return "";
    else if (!siteData.is_active) return "SITE IS INACTIVE";
    else if (siteData.online !== 1 && siteData.bl_online !== 1) return "BOX IS CURRENTLY OFFLINE";
    else if (siteData.online !== 1 && siteData.bl_online === 1) return "CLIENT IS CURRENTLY OFFLINE";
    else return "BOOTLOADER IS CURRENTLY OFFLINE";
  };

  const SiteConnectionStatus = () => {
    if (
      !siteData ||
      (siteData.is_active && siteData.online === 1 && siteData.bl_online === 1) ||
      elionBoxStatus?.STATUS == 2
    ) {
      return null;
    }

    return (
        <Box height="35px" display="flex" alignItems="center" justifyContent="space-between"
             sx={{borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(235, 49, 49, 0.85)', px: '30%'}}>
            <ErrorIcon htmlColor="white"/>
            <Typography noWrap color="white">{connectionStatusText()}</Typography>
            <ErrorIcon htmlColor="white"/>
        </Box>
    );
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "white", pl: 3 }}>
        <Tabber
          value={tab}
          onChange={(event, newValue) => changeTab(newValue)}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="General" value={Tabs.GENERAL} />
          <Tab label="Alerts" value={Tabs.ALERTS} />
          <Tab label={formatDevices(siteData ? siteData.devices : [])} value={Tabs.DEVICES} />
          <Tab label="Installation" value={Tabs.INSTALLATION} />
          <Tab label="Schematic" value={Tabs.SCHEMATIC} />
          <Tab label="Commissioning" value={Tabs.COMMISSIONING} />
          <Tab label="Metering" value={Tabs.METERING} />
          <Tab label="Statistics" value={Tabs.STATISTICS} />
          <Tab label="Financial" value={Tabs.FINANCIAL} />
          <Tab label="Log" value={Tabs.LOG} />
          {/*<Tab label="Dashboard" value={Tabs.DASHBOARD}/>*/}
        </Tabber>
      </Box>

      {!siteError && !siteLoading ? <SiteConnectionStatus /> : null}

      <Container width="100%" height="100%" spacing={2} sx={{ marginBottom: 2, marginTop: 2 }} maxWidth="xl">
        <Outlet />
      </Container>
    </>
  );
}
